import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type AgentSessionMessageResponse, type ListAgentSessionMessagesResponse } from '@air/server-sdk';

import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  collectMissedWorkspaceAgentCatchUpMessages,
  createOptimisticWorkspaceAgentMessage,
  EMPTY_WORKSPACE_AGENT_MESSAGE_STORE,
  getWorkspaceAgentMessagesForSession,
  mergeServerWorkspaceAgentMessages,
  removeOptimisticWorkspaceAgentMessage,
  shouldApplyWorkspaceAgentCatchUpServerMessage,
  sortWorkspaceAgentMessages,
  stampWorkspaceAgentMessagesSession,
  upsertWorkspaceAgentMessage,
  type WorkspaceAgentMessageStore,
  type WorkspaceAgentMessagesById,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMessageStore';

export type UsePrivateWorkspaceAgentMessageStoreParams = {
  /** Read at call time, so a message arriving as the session switches is filed against the new one. */
  getActiveSessionId: () => string | null;
  serverPages: ListAgentSessionMessagesResponse[] | undefined;
  sessionId: string | null;
  workspaceId: string;
};

export type AddOptimisticUserMessageParams = {
  clientMessageId: string;
  content: string;
  metadata: Record<string, unknown> | undefined;
  /** Null for the message that starts a conversation, which has no session to be filed against. */
  targetSessionId: string | null;
};

export type RemoveOptimisticUserMessageParams = {
  clientMessageId: string;
  targetSessionId: string | null;
};

export type UsePrivateWorkspaceAgentMessageStoreResult = {
  addOptimisticUserMessage: (params: AddOptimisticUserMessageParams) => void;
  /** Hands the rows written before a session existed to the session that has just been started. */
  adoptOptimisticMessagesSession: (sessionId: string) => void;
  /** Synchronous read of the active session's messages — WSS toolCall/toolResult pairing and run-finish cache invalidation. */
  getMessagesSnapshot: () => WorkspaceAgentMessage[];
  /**
   * Folds in a REST page without dropping rows the page omitted — live catch-up lists only the
   * latest messages, and replacing the store with that page would flash older history out.
   * Returns rows that were missing from the live store (not optimistic send confirmations).
   */
  mergeServerMessages: (serverMessages: AgentSessionMessageResponse[]) => AgentSessionMessageResponse[];
  messages: WorkspaceAgentMessage[];
  removeOptimisticUserMessage: (params: RemoveOptimisticUserMessageParams) => void;
  upsertMessage: (message: WorkspaceAgentMessage) => void;
};

/** Holds the messages for the active session, merging server pages with live and optimistic rows. */
export const usePrivateWorkspaceAgentMessageStore = ({
  getActiveSessionId,
  serverPages,
  sessionId,
  workspaceId,
}: UsePrivateWorkspaceAgentMessageStoreParams): UsePrivateWorkspaceAgentMessageStoreResult => {
  const [messageStore, setMessageStore] = useState<WorkspaceAgentMessageStore>(EMPTY_WORKSPACE_AGENT_MESSAGE_STORE);
  const messagesSnapshotRef = useRef<WorkspaceAgentMessage[]>([]);

  useIsomorphicLayoutEffect(() => {
    if (!serverPages) {
      return;
    }

    setMessageStore((current) => ({
      sessionId,
      byId: serverPages.reduce(
        (byId, page) => mergeServerWorkspaceAgentMessages(byId, page.data),
        getWorkspaceAgentMessagesForSession(current, sessionId),
      ),
    }));
  }, [serverPages, sessionId]);

  const messages = useMemo(
    () => sortWorkspaceAgentMessages(getWorkspaceAgentMessagesForSession(messageStore, sessionId)),
    [messageStore, sessionId],
  );

  useEffect(() => {
    messagesSnapshotRef.current = messages;
  }, [messages]);

  const getMessagesSnapshot = useCallback(() => messagesSnapshotRef.current, []);

  const syncSnapshotForActiveSession = useCallback(
    (targetSessionId: string | null, byId: WorkspaceAgentMessagesById) => {
      if (targetSessionId === getActiveSessionId()) {
        messagesSnapshotRef.current = sortWorkspaceAgentMessages(byId);
      }
    },
    [getActiveSessionId],
  );

  const upsertMessage = useCallback(
    (message: WorkspaceAgentMessage) => {
      const applyMessage = () =>
        setMessageStore((current) => {
          const activeSessionId = getActiveSessionId();

          if (message.sessionId !== activeSessionId) {
            return current;
          }

          const nextById = upsertWorkspaceAgentMessage(
            getWorkspaceAgentMessagesForSession(current, activeSessionId),
            message,
          );

          messagesSnapshotRef.current = sortWorkspaceAgentMessages(nextById);

          return {
            sessionId: activeSessionId,
            byId: nextById,
          };
        });

      // A toolCall and its toolResult usually arrive in the same tick. Without flushing, React
      // batches them into one render and the in-progress state never paints.
      if (message.role === 'toolCall') {
        flushSync(applyMessage);

        return;
      }

      applyMessage();
    },
    [getActiveSessionId],
  );

  const addOptimisticUserMessage = useCallback(
    ({ clientMessageId, content, metadata, targetSessionId }: AddOptimisticUserMessageParams) => {
      setMessageStore((current) => {
        const byId = getWorkspaceAgentMessagesForSession(current, targetSessionId);
        const optimistic = createOptimisticWorkspaceAgentMessage({
          byId,
          clientMessageId,
          content,
          metadata,
          sessionId: targetSessionId,
          workspaceId,
        });
        const nextById = { ...byId, [optimistic.id]: optimistic };

        syncSnapshotForActiveSession(targetSessionId, nextById);

        return { sessionId: targetSessionId, byId: nextById };
      });
    },
    [syncSnapshotForActiveSession, workspaceId],
  );

  const adoptOptimisticMessagesSession = useCallback(
    (nextSessionId: string) => {
      setMessageStore((current) => {
        // Only the session-less rows are the starting conversation's. A store already filed against a
        // session belongs to a conversation that was picked or adopted while `start` was in flight, and
        // taking its rows would move them into a thread they were never sent to.
        if (current.sessionId !== null) {
          return current;
        }

        const nextById = stampWorkspaceAgentMessagesSession(current.byId, nextSessionId);

        syncSnapshotForActiveSession(nextSessionId, nextById);

        return { sessionId: nextSessionId, byId: nextById };
      });
    },
    [syncSnapshotForActiveSession],
  );

  const mergeServerMessages = useCallback(
    (serverMessages: AgentSessionMessageResponse[]): AgentSessionMessageResponse[] => {
      const activeSessionId = getActiveSessionId();

      if (activeSessionId === null) {
        return [];
      }

      const currentById: WorkspaceAgentMessagesById = {};

      for (const message of getMessagesSnapshot()) {
        currentById[message.id] = message;
      }

      const forActiveSession = serverMessages.filter((message) => message.sessionId === activeSessionId);
      const messagesToMerge = forActiveSession.filter((message) =>
        shouldApplyWorkspaceAgentCatchUpServerMessage(currentById[message.id], message),
      );

      if (messagesToMerge.length === 0) {
        return [];
      }

      const missedForAnalytics = collectMissedWorkspaceAgentCatchUpMessages(currentById, messagesToMerge);

      setMessageStore((current) => {
        if (getActiveSessionId() !== activeSessionId) {
          return current;
        }

        const storeById = getWorkspaceAgentMessagesForSession(current, activeSessionId);
        const stillToMerge = forActiveSession.filter((message) =>
          shouldApplyWorkspaceAgentCatchUpServerMessage(storeById[message.id], message),
        );

        if (stillToMerge.length === 0) {
          return current;
        }

        const nextById = mergeServerWorkspaceAgentMessages(storeById, stillToMerge);

        messagesSnapshotRef.current = sortWorkspaceAgentMessages(nextById);

        return {
          sessionId: activeSessionId,
          byId: nextById,
        };
      });

      return missedForAnalytics;
    },
    [getActiveSessionId, getMessagesSnapshot],
  );

  const removeOptimisticUserMessage = useCallback(
    ({ clientMessageId, targetSessionId }: RemoveOptimisticUserMessageParams) => {
      setMessageStore((current) => {
        // The send that failed may have been left behind by a session switch, in which case its row
        // is already gone and the store on screen belongs to someone else.
        if (current.sessionId !== targetSessionId) {
          return current;
        }

        const nextById = removeOptimisticWorkspaceAgentMessage(current.byId, clientMessageId);

        syncSnapshotForActiveSession(targetSessionId, nextById);

        return {
          sessionId: targetSessionId,
          byId: nextById,
        };
      });
    },
    [syncSnapshotForActiveSession],
  );

  return {
    addOptimisticUserMessage,
    adoptOptimisticMessagesSession,
    getMessagesSnapshot,
    mergeServerMessages,
    messages,
    removeOptimisticUserMessage,
    upsertMessage,
  };
};
